import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CardActionArea from "@mui/material/CardActionArea";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import ListAltIcon from "@mui/icons-material/ListAltOutlined";
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined";
import CloseIcon from "@mui/icons-material/Close";

import { useTranslation } from "react-i18next";
import { notificationsApi } from "../api/endpoints";
import { IconoMorfo } from "./IconoMorfo";
import { useLocale } from "../hooks/useLocale";
import { formatDateTime } from "../utils/format";
import { brand, velo } from "../theme/theme";

/** A shared notification inbox: anchored on desktop, below the mobile header. */
export function MobileNotificationsPanel({ open, anchorEl, onClose, onUnreadChange }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const mobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const [data, setData] = useState({ notifications: [], unreadCount: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    notificationsApi
      .list()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(false);
        onUnreadChange(result.unreadCount ?? 0);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, onUnreadChange]);

  const abrir = async (notification) => {
    if (!notification.readAt) {
      try {
        await notificationsApi.markRead(notification.id);
        const unreadCount = Math.max(0, (data.unreadCount ?? 0) - 1);
        onUnreadChange(unreadCount);
        setData((actual) => {
          return {
            ...actual,
            unreadCount,
            notifications: actual.notifications.map((item) =>
              item.id === notification.id
                ? { ...item, readAt: new Date().toISOString() }
                : item,
            ),
          };
        });
      } catch {
        // La navegación no debe quedar bloqueada por el acuse de lectura.
      }
    }

    onClose();
    navigate(notification.linkPath || "/notificaciones");
  };

  const verTodas = () => {
    onClose();
    navigate("/notificaciones");
  };

  const markAll = async () => {
    setBusy(true);
    try {
      await notificationsApi.markAllRead();
      setData((actual) => ({
        ...actual,
        unreadCount: 0,
        notifications: actual.notifications.map((notification) => ({
          ...notification,
          readAt: notification.readAt || new Date().toISOString(),
        })),
      }));
      onUnreadChange(0);
      window.dispatchEvent(
        new CustomEvent("atlas:notifications-changed", {
          detail: { unreadCount: 0 },
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      await notificationsApi.clear();
      setData({ notifications: [], unreadCount: 0 });
      onUnreadChange(0);
      window.dispatchEvent(
        new CustomEvent("atlas:notifications-changed", {
          detail: { unreadCount: 0 },
        }),
      );
      setDeleteOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const notifications = data.notifications?.slice(0, 8) ?? [];
  const Panel = mobile ? Drawer : Popover;
  const panelProps = mobile ? {
    anchor: "top",
    ModalProps: { keepMounted: true, disableEnforceFocus: true },
    slotProps: {
      backdrop: { sx: { top: 68, bottom: "calc(56px + env(safe-area-inset-bottom))", bgcolor: "rgba(10, 15, 27, 0.38)", pointerEvents: "auto" } },
      paper: { id: "mobile-notifications-panel", role: "dialog", "aria-label": t("notifications.title") },
    },
    sx: {
      pointerEvents: "none",
      "& .MuiDrawer-paper": {
        pointerEvents: "auto", top: 68, bottom: "auto", height: "auto", width: "100%",
        maxHeight: "min(76dvh, calc(100dvh - 124px - env(safe-area-inset-bottom)))",
        boxSizing: "border-box", borderRadius: "0 0 12px 12px", overflow: "hidden",
        boxShadow: "0 18px 42px rgba(10, 15, 27, 0.2)", borderColor: "divider", bgcolor: "background.paper", backgroundImage: "none",
      },
    },
  } : {
    anchorEl,
    anchorOrigin: { vertical: "bottom", horizontal: "right" },
    transformOrigin: { vertical: "top", horizontal: "right" },
    marginThreshold: 12,
    slotProps: { paper: {
      id: "mobile-notifications-panel", role: "dialog", "aria-label": t("notifications.title"),
      sx: { mt: 3, width: 440, maxWidth: "calc(100vw - 24px)", maxHeight: "min(76dvh, 680px)", borderRadius: "12px", overflow: "hidden", border: "1px solid", borderColor: "divider", backgroundImage: "none" },
    } },
  };

  return (
    <>
    <Panel
      {...panelProps}
      open={open}
      onClose={onClose}
      transitionDuration={{ enter: 360, exit: 260 }}
    >
      <Stack sx={{ minHeight: 0, maxHeight: "inherit" }}>
        <Stack
          direction="row"
          spacing={1.25}
          sx={{ px: 2.25, py: 1.5, alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
        >
          <Box sx={{ flex: "1 1 140px", minWidth: 0 }}>
            <Typography variant="h3">{t("notifications.title")}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t("notifications.panelUnread", {
                count: data.unreadCount ?? 0,
              })}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ ml: "auto" }}>
            <Tooltip title={t("notifications.markAllRead")}>
              <Box component="span">
                <IconButton
                  size="small"
                  onClick={markAll}
                  disabled={busy || (data.unreadCount ?? 0) === 0}
                  aria-label={t("notifications.markAllRead")}
                >
                  <IconoMorfo
                    trazo={(data.unreadCount ?? 0) > 0 ? "marcarLeidas" : "visto"}
                    size={20}
                  />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title={t("notifications.deleteAll")}>
              <Box component="span">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteOpen(true)}
                  disabled={busy || (data.notifications?.length ?? 0) === 0}
                  aria-label={t("notifications.deleteAll")}
                >
                  <IconoMorfo
                    trazo={deleteOpen ? "cerrar" : "papelera"}
                    size={20}
                  />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title={t("common.close")}>
              <IconButton size="small" onClick={onClose} aria-label={t("common.close")}><CloseIcon /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Divider />

        <Box sx={{ flex: 1, minHeight: 0, maxHeight: "min(55dvh, 500px)", overflowY: "auto", overscrollBehavior: "contain" }}>
          {loading ? (
            <Box sx={{ py: 6, display: "grid", placeItems: "center" }}>
              <CircularProgress size={26} />
            </Box>
          ) : error ? (
            <Stack spacing={1.5} sx={{ px: 2.25, py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {t("notifications.panelError")}
              </Typography>
              <Button size="small" onClick={() => onClose()}>
                {t("common.close")}
              </Button>
            </Stack>
          ) : notifications.length === 0 ? (
            <Box sx={{ px: 2.25, py: 5, textAlign: "center" }}>
              <Typography variant="subtitle2">
                {t("notifications.emptyTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("notifications.emptyText")}
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {notifications.map((notification) => {
                const unread = !notification.readAt;
                const Icon = notification.linkPath?.includes("/presupuestos")
                  ? ReceiptIcon
                  : ListAltIcon;

                return (
                  <CardActionArea
                    key={notification.id}
                    onClick={() => abrir(notification)}
                    sx={{ px: 2.25, py: 1.75 }}
                  >
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          bgcolor: unread ? velo(brand.violet, 10) : "action.hover",
                          color: unread ? brand.violet : "text.secondary",
                        }}
                      >
                        <Icon sx={{ fontSize: 19 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2">
                          {t(notification.titleKey, {
                            ...notification.params,
                            defaultValue: notification.titleKey,
                          })}
                        </Typography>
                        {notification.bodyKey && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.25 }}
                          >
                            {t(notification.bodyKey, {
                              ...notification.params,
                              defaultValue: notification.bodyKey,
                            })}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(notification.createdAt, locale)}
                        </Typography>
                      </Box>
                      {unread && (
                        <Box
                          aria-label={t("notifications.unread")}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: brand.violet,
                            mt: 0.75,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Stack>
                  </CardActionArea>
                );
              })}
            </Stack>
          )}
        </Box>

        <Divider />
        <Button onClick={verTodas} sx={{ m: 1.25 }}>
          {t("notifications.viewAll")}
        </Button>
      </Stack>
    </Panel>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t("notifications.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("notifications.deleteConfirmText")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={clearAll}
            disabled={busy}
          >
            {t("notifications.deleteAll")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
